import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, GitCompareArrows } from "lucide-react";
import GinCard from "../components/GinCard";
import { filterGins, getMeta } from "../lib/api";
import { recordEvent } from "../lib/adminApi";

const FILTER_GROUPS = [
  { key: "moods", label: "Mood", source: "moods" },
  { key: "tastes", label: "Taste", source: "tastes" },
  { key: "styles", label: "Style", source: "styles" },
  { key: "countries", label: "Country", source: "countries" },
  { key: "price_bands", label: "Price", source: "price_bands" },
  { key: "serves", label: "Serve", source: "serves" },
];

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({
    moods: [], tastes: [], styles: [], countries: [], price_bands: [], serves: [],
  });
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareList, setCompareList] = useState(() => {
    try { return JSON.parse(localStorage.getItem("beno-compare") || "[]"); } catch { return []; }
  });
  const [activeGroup, setActiveGroup] = useState("moods");

  // Init from URL
  useEffect(() => {
    const next = { ...filters };
    if (searchParams.get("taste")) next.tastes = [searchParams.get("taste")];
    if (searchParams.get("country")) next.countries = [searchParams.get("country")];
    if (searchParams.get("style")) next.styles = [searchParams.get("style")];
    setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { getMeta().then(setMeta); }, []);

  useEffect(() => {
    setLoading(true);
    filterGins({ ...filters, search: search.trim() || null }).then((d) => {
      setResults(d.gins);
      setLoading(false);
    });
    if (search.trim().length >= 2) recordEvent("search", { query: search.trim() });
  }, [filters, search]);

  useEffect(() => {
    localStorage.setItem("beno-compare", JSON.stringify(compareList));
  }, [compareList]);

  const toggle = (group, value) => {
    setFilters((f) => {
      const cur = f[group] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...f, [group]: next };
    });
    recordEvent("filter_select", { filter_group: group, filter_value: value });
  };

  const clearAll = () => {
    setFilters({ moods: [], tastes: [], styles: [], countries: [], price_bands: [], serves: [] });
    setSearch("");
    setSearchParams({});
  };

  const activeCount = useMemo(
    () => Object.values(filters).reduce((s, arr) => s + arr.length, 0) + (search.trim() ? 1 : 0),
    [filters, search]
  );

  const toggleCompare = (id) => {
    setCompareList((list) => list.includes(id) ? list.filter((i) => i !== id) : list.length >= 3 ? [list[1], list[2], id] : [...list, id]);
  };

  if (!meta) {
    return <div className="px-6 py-32 text-center text-white/50">Loading the atlas...</div>;
  }

  const renderOptions = (group) => {
    const items = meta[group.source] || [];
    return items.map((item) => {
      const val = group.source === "price_bands" ? item.id : item;
      const label = group.source === "price_bands" ? item.label : item;
      const active = (filters[group.key] || []).includes(val);
      return (
        <button
          key={val}
          onClick={() => toggle(group.key, val)}
          className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-all ${
            active
              ? "bg-[#C17F59] text-[#0E0F0C] border-[#C17F59]"
              : "border-white/15 text-white/70 hover:border-[#C17F59] hover:text-[#C17F59]"
          }`}
          data-testid={`filter-${group.key}-${val}`}
        >
          {label}
        </button>
      );
    });
  };

  return (
    <div className="px-5 md:px-12 max-w-7xl mx-auto pb-20" data-testid="explore-page">
      {/* Header */}
      <div className="py-8 border-b border-white/5">
        <p className="overline text-[#C17F59] mb-2">Explore</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">The Atlas</h1>
        <p className="text-white/60 max-w-2xl mt-2">Filter 92 gins by mood, taste, style, country, price, or serve.</p>
      </div>

      {/* Search */}
      <div className="py-6 flex items-center gap-3 border-b border-white/5">
        <Search size={16} className="text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search gin, country, botanical..."
          className="flex-1 bg-transparent text-sm placeholder:text-white/30 focus:outline-none"
          data-testid="search-input"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-white/40 hover:text-white" data-testid="search-clear">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filter group tabs */}
      <div className="py-4 flex flex-wrap gap-1 border-b border-white/5 overflow-x-auto no-scrollbar">
        {FILTER_GROUPS.map((g) => {
          const count = (filters[g.key] || []).length;
          const active = activeGroup === g.key;
          return (
            <button
              key={g.key}
              onClick={() => setActiveGroup(g.key)}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors ${
                active ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"
              }`}
              data-testid={`filter-group-${g.key}`}
            >
              {g.label}
              {count > 0 && <span className="ml-2 text-[#D89A74]">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Filter chips */}
      <motion.div
        layout
        className="py-5 flex flex-wrap gap-2 min-h-[60px]"
        data-testid="filter-options"
      >
        {renderOptions(FILTER_GROUPS.find((g) => g.key === activeGroup))}
      </motion.div>

      {/* Active summary */}
      <div className="flex items-center justify-between border-b border-white/5 pb-4">
        <p className="text-sm text-white/60">
          <span className="text-white text-lg font-display">{results.length}</span>
          {" "}gins
          {activeCount > 0 && <span className="text-white/40"> · {activeCount} filter{activeCount > 1 ? "s" : ""} on</span>}
        </p>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#C17F59] transition-colors" data-testid="filter-clear-all">
            Reset filters
          </button>
        )}
      </div>

      {/* Results grid */}
      <div className="pt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="gin-grid">
        {loading && <p className="text-white/40 col-span-full">Reading the atlas...</p>}
        {!loading && results.length === 0 && (
          <div className="col-span-full text-center py-20 text-white/50">
            <p className="font-display text-2xl mb-2">No gin matches that combination.</p>
            <p className="text-sm">Try fewer filters or hit reset above.</p>
          </div>
        )}
        {results.map((g, i) => (
          <GinCard
            key={g.id}
            gin={g}
            index={i}
            onCompareToggle={toggleCompare}
            isCompared={compareList.includes(g.id)}
          />
        ))}
      </div>

      {/* Floating compare bar */}
      {compareList.length > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-30 bg-[#15171A] border border-[#C17F59]/40 px-5 py-3 flex items-center gap-4 backdrop-blur-md"
          data-testid="compare-floating-bar"
        >
          <span className="text-xs uppercase tracking-[0.22em] text-white/70">
            {compareList.length} selected
          </span>
          <Link
            to={`/gin/compare?ids=${compareList.join(",")}`}
            className="flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#D89A74] transition-colors"
            data-testid="compare-go"
          >
            <GitCompareArrows size={14} />
            Compare
          </Link>
          <button onClick={() => setCompareList([])} className="text-white/40 hover:text-white" data-testid="compare-clear">
            <X size={14} />
          </button>
        </motion.div>
      )}
    </div>
  );
}
